'use client';
import React from 'react';
import { School2 } from 'lucide-react';
import type { School } from '@/data/schools';
import SchoolCard from './SchoolCard';

interface SearchResultsGridProps {
  schools: School[];
  communityFilter: string;
  compareList: string[];
  onToggleCompare: (id: string) => void;
  onSave: (id: string) => void;
  savedList: string[];
  isLoading: boolean;
  selectedSchoolId?: string | null;
  onSelectSchool?: (id: string | null) => void;
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden animate-pulse">
      <div className="h-32 bg-muted" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-16 bg-muted rounded-lg" />
          <div className="h-16 bg-muted rounded-lg" />
        </div>
        <div className="h-8 bg-muted rounded-lg" />
        <div className="h-10 bg-muted rounded-lg" />
      </div>
    </div>
  );
}

export default function SearchResultsGrid({
  schools,
  communityFilter,
  compareList,
  onToggleCompare,
  onSave,
  savedList,
  isLoading,
}: SearchResultsGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={`skel-${i + 1}`} />
        ))}
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
          <School2 size={28} className="text-muted-foreground" />
        </div>
        <h3 className="font-display font-semibold text-lg text-foreground mb-2">No schools match your filters</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Try broadening your search — remove a curriculum filter, expand your fee range, or select a different KHDA rating.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
      {schools.map((school) => (
        <SchoolCard
          key={`result-${school.id}`}
          school={school}
          communityFilter={communityFilter}
          compareList={compareList}
          onToggleCompare={onToggleCompare}
          onSave={onSave}
          savedList={savedList}
        />
      ))}
    </div>
  );
}